<template>
  <ElForm
    :model="guest"
    :rules="guestDetailsFormRules"
    ref="form"
    label-position="top"
    label-width="120px"
    status-icon
    :disabled="disableForm"
    @submit.native.prevent
  >
    <div
      class="guest-grid grid col-gap-20 px-20 mobile-m:col-gap-25 mobile-m:px-25 tablet:col-gap-30 tablet:px-30">
      <div class="flex items-center justify-center mobile-m:row-start-1 mobile-m:row-end-3">
        <div class="relative mb-15">
          <UIInputFile
            v-model="photo.file"
            accept=".png, .jpg, .jpeg"
            @mouseover.native="hoveringAvatar = true"
            @mouseleave.native="hoveringAvatar = false"
            @input="hoveringAvatar = false"
            @change="photo.edited = true"
          >
            <template #default="{ selectFile, removeFile }">
              <UIAvatar
                :image-src="avatarPhoto"
                :image-alt="`Photo of ${guestFullName}`"
                :title="guestFullName"
                class="guest-avatar bg-primary-light shadow-1dp"
                @touchend="onAvatarTouch($event, selectFile)"
              >
                <FontAwesomeIcon
                  :icon="['fad', 'user-circle']"
                  class="icon-no-image-placeholder fa-swap-opacity h-120"
                />
              </UIAvatar>
              <Transition name="fade-slow">
                <button
                  v-show="hoveringAvatar || (showDefaultAddPhoto && !avatarPhoto)"
                  class="absolute inset-0 h-full w-full flex items-center justify-center text-white focus:outline-none"
                  @click="selectFile"
                >
                  <span class="absolute inset-0 bg-primary opacity-60 rounded-full"></span>
                  <span class="absolute inset-0 flex items-center justify-center text-white">
                    {{ avatarPhoto ? 'Edit' : 'Add Photo' }}
                  </span>
                </button>
              </Transition>
              <Transition name="fade-scale">
                <button
                  v-if="avatarPhoto"
                  v-show="hoveringAvatar"
                  title="Press to remove the guest photo"
                  class="absolute right-0 top-0 w-35 h-35 flex items-center justify-center bg-danger rounded-full
                  shadow-1dp focus:outline-none focus:shadow-6dp hover:shadow-6dp"
                  @click.prevent="onRemovePhotoPress(removeFile)"
                >
                  <FontAwesomeIcon
                    :icon="['far', 'times']"
                    class="text-xl text-white"
                  />
                </button>
              </Transition>
            </template>
          </UIInputFile>
          <Transition name="fade-scale">
            <a
              v-if="guestBadge"
              :href="guestBadge"
              target="_blank"
              title="Press to open the badge in a new tab"
              class="absolute left-0 bottom-0 w-42 h-42 flex items-center justify-center bg-primary rounded-full
              shadow-1dp cursor-pointer focus:outline-none focus:opacity-90 hover:opacity-90"
            >
              <FontAwesomeIcon
                :icon="['far', 'id-badge']"
                class="text-2xl text-white"
              />
            </a>
          </Transition>
          <Transition name="fade-scale">
            <a
              v-if="showQrCode && QRCodeURL"
              :href="QRCodeURL"
              target="_blank"
              :title="`Press to open the ${QrCodeType === 'Code39' ? 'Barcode': 'QR-Code'} in a new tab`"
              class="absolute right-0 bottom-0 w-42 h-42 flex items-center justify-center bg-primary rounded-full
              shadow-1dp cursor-pointer focus:outline-none focus:opacity-90 hover:opacity-90"
            >
              <FontAwesomeIcon
                :icon="QrCodeType === 'Code39' ? ['fal', 'barcode'] : ['fal', 'qrcode']"
                class="text-2xl text-gray-100"
              />
            </a>
          </Transition>
        </div>
      </div>
      <ElFormItem
        v-if="firstNameFirstOrder"
        label="First name"
        prop="firstName"
        class="tablet:-mr-15"
      >
        <ElInput
          v-model="guest.firstName"
          ref="input-first-name"
          placeholder="Joeven"
          data-private
        />
      </ElFormItem>
      <ElFormItem
        label="Last name"
        prop="lastName"
      >
        <ElInput
          v-model="guest.lastName"
          ref="input-last-name"
          placeholder="Doe"
          data-private
        />
      </ElFormItem>
      <ElFormItem
        v-if="!firstNameFirstOrder"
        label="First name"
        prop="firstName"
      >
        <ElInput
          v-model="guest.firstName"
          placeholder="Joeven"
          data-private
        />
      </ElFormItem>
      <ElFormItem
        prop="email"
        class="mobile-m:col-start-1 mobile-m:col-end-3 tablet:col-start-2 tablet:col-end-4"
      >
        <template slot="label">
          Email <span class="font-normal text-sm italic">- Optional</span>
        </template>
        <ElInput
          v-model.trim="guest.email"
          placeholder="name@example.com"
          inputmode="email"
          data-private
        />
      </ElFormItem>
    </div>

    <div class="w-full flex px-20 -mx-10 mobile-m:px-25 tablet:px-30">
      <ElFormItem
        label="Guest status"
        prop="status"
        class="mobile-l:w-1/2 px-10"
      >
        <ElSelect
          v-model="guest.status"
          placeholder="Select a status"
        >
          <ElOption
            v-for="option in statusOptions"
            :key="option.value"
            :label="option.label"
            :value="option.value">
          </ElOption>
        </ElSelect>
      </ElFormItem>
      <ElFormItem
        label="VIP"
        prop="vip"
        class="mobile-l:w-1/2 px-10"
      >
        <ElSwitch v-model="guest.vip" />
      </ElFormItem>
    </div>
    <TransitionCollapse>
      <p
        v-if="[0, 4, 5].includes(guest.status)"
        class="px-20 mobile-m:px-25 tablet:px-30"
      >
        <span
          class="flex flex-col items-center p-15 mb-20 bg-gray-100 rounded-20 tablet:flex-row"
        >
          <FontAwesomeIcon
            :icon="['fas', 'info-circle']"
            class="text-2xl text-warning mb-10 tablet:mb-0 tablet:mr-15"
          />
          <span
            class="font-medium text-sm text-gray-800 text-center leading-tight tablet:text-left">
            Guests with this status will not be visible in the Android and iOS Apps
          </span>
        </span>
      </p>
    </TransitionCollapse>

    <ElFormItem
      prop="totalExtraGuests"
      label="Total extra guests"
      class="px-20 mobile-m:px-25 tablet:px-30"
    >
      <ElInputNumber
        v-model="guest.totalExtraGuests"
        :disabled="maxExtraGuests === 0 && guest.totalExtraGuests === 0"
        :min="0"
        :max="maxExtraGuests"
        :title="'Enter the number of extra guests'"
      />
    </ElFormItem>

    <TransitionCollapse>
      <p
        v-if="showMaxExtraGuestsReached"
        class="px-20 mb-20 mobile-m:px-25 tablet:px-30"
      >
        <span class="flex flex-col p-15 bg-gray-100 rounded-20">
          <span class="flex flex-col items-center tablet:flex-row">
          <FontAwesomeIcon
            :icon="['fas', 'info-circle']"
            class="text-2xl text-warning mb-10 tablet:mb-0 tablet:mr-15"
          />
          <span class="font-medium text-sm text-gray-800 text-center leading-tight tablet:text-left">
            The event reached the guest limit,
            <template v-if="maxExtraGuests">
              you can add up to {{ maxExtraGuests }} extra {{ maxExtraGuests === 1 ? 'guest' : 'guests' }}
            </template>
            <template v-else>
              you can't add more extra guests
            </template>
          </span>
        </span>
        <button
          v-if="canUpgrade"
          class="flex items-center justify-center font-medium text-primary mt-10 hover:underline focus:underline
          focus:outline-none"
          @click="$emit('upgrade-event')"
        >
          <FontAwesomeIcon
            fixed-width
            :icon="['far', 'angle-double-up']"
            class="text-2xl mr-2"
          />
          Upgrade event
        </button>
        </span>
      </p>
    </TransitionCollapse>

    <ElFormItem
      v-if="showBarcodeValueInput"
      prop="barcodeValue"
      label="Barcode value"
      class="px-20 mobile-m:px-25 tablet:px-30"
    >
      <ElInput
        v-model.trim="guest.barcodeValue"
        placeholder="Ex. 5de0f201b573d"
        data-private
        @input="guest.barcodeValue = $event.replace(/\s+/g, '')"
      />
    </ElFormItem>

    <UIToggle
      v-model="additionalInfoIsOpen"
      ref="additional-infos"
      label="Additional guest infos"
      label-classes="w-full flex items-center py-8 px-20 rounded-t-5 border-b border-gray-200 transition-colors
      duration-200 ease-in-out-quart focus:outline-none focus:border-gray-400 hover:border-gray-400 mobile-m:px-0"
      content-classes="px-20 mobile-m:px-0"
      class="mt-25 mobile-m:mx-25 tablet:mx-30"
      @shown="onAdditionalInfoShown"
    >
      <template #label>
        <h3 class="text-lg">
          Additional infos <span class="font-normal text-sm italic">- Optional</span>
        </h3>
        <FontAwesomeIcon
          :icon="['far', 'chevron-right']"
          class="text-base text-primary-light ml-20 transform rotate-0 duration-300 ease-in-out-quart"
          :class="{ 'rotate-90': additionalInfoIsOpen }"
        />
      </template>
      <ElFormItem
        label="Phone number"
        prop="phoneNumber"
        class="mt-10"
      >
        <ElInput
          v-model="guest.phoneNumber"
          :placeholder="phonePlaceholder"
          inputmode="tel"
        />
      </ElFormItem>
      <div class="flex flex-col -mx-10 mobile-l:flex-row">
        <ElFormItem
          label="Job title"
          prop="jobTitle"
          class="px-10"
        >
          <ElInput
            v-model="guest.jobTitle"
            placeholder="Head of Sales"
          />
        </ElFormItem>
        <ElFormItem
          label="Organization"
          prop="organization"
          class="px-10"
        >
          <ElInput
            v-model="guest.organization"
            placeholder="Acme Corporation"
          />
        </ElFormItem>
      </div>
      <ElFormItem
        label="Note"
        prop="note"
      >
        <ElInput
          v-model="guest.note"
          type="textarea"
          placeholder="Add a note..."
          :autosize="{ minRows: 2, maxRows: 4}"
        />
      </ElFormItem>
    </UIToggle>
    <slot :submitForm="submitForm" />
  </ElForm>
</template>

<script>
import { getQRCodeURL, getBarcodeURL } from '@/utils/barcodeGeneration';
import { phoneNumberIsAcceptable } from '@/utils/phone';
import { phonePlaceholder } from '@/utils/placeholders';
import UIToggle from '@/components/UI/UIToggle';
import UIAvatar from '@/components/UI/UIAvatar';
import UIInputFile from '@/components/UI/input/UIInputFile';
import TransitionCollapse from '@/components/transition/TransitionCollapse';

const defaultGuestData = {
  firstName: '',
  lastName: '',
  email: '', // (Optional)
  status: 1, // (Default: 1 - Attending)
  vip: false,
  totalExtraGuests: 0,
  phoneNumber: '',
  jobTitle: '',
  organization: '',
  note: '',
  barcodeValue: '',
};

export default {
  props: {
    autofocusFirstInput: {
      type: Boolean,
      default: false,
    },
    showDefaultAddPhoto: {
      type: Boolean,
      default: false,
    },
    showBarcodeValueInput: {
      type: Boolean,
      default: false,
    },
    showQrCode: {
      type: Boolean,
      default: false,
    },
    QrCodeType: {
      type: String,
      default: 'QRCode',
    },
    firstNameFirstOrder: {
      type: Boolean,
      default: false,
    },
    existingGuestData: {
      type: Object,
      default: null,
    },
    guestPhoto: {
      type: String,
      default: '',
    },
    guestBadge: {
      type: String,
      default: '',
    },
    disableForm: {
      type: Boolean,
      default: false,
    },
    statusOptions: {
      type: Array,
      default: () => ([]),
    },
    maxExtraGuests: {
      type: Number,
      default: 0,
    },
    showMaxExtraGuestsReached: {
      type: Boolean,
      default: false,
    },
    canUpgrade: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    TransitionCollapse,
    UIToggle,
    UIAvatar,
    UIInputFile,
  },
  data() {
    const checkFirstName = (rule, value, callback) => {
      if (!value || value === '') {
        callback('Enter a first name');
      }
      callback();
    };
    const checkLastName = (rule, value, callback) => {
      if (!value || value === '') {
        callback('Enter a last name');
      }
      callback();
    };
    const originalPhoneNumber = this.existingGuestData.phoneNumber || '';
    const validatePhoneNumber = (rule, value, callback) => {
      const phoneNumber = `${value || ''}`.trim();

      // A legacy value that is already stored stays editable, so an old bad number cannot block a save.
      if (phoneNumber === '' || phoneNumber === `${originalPhoneNumber}`.trim()) {
        callback();

        return;
      }
      if (!phoneNumberIsAcceptable(phoneNumber)) {
        callback('Enter a valid phone number, e.g. +41 79 123 45 67');

        return;
      }
      callback();
    };
    const validateEmail = (rule, value, callback) => {
      const re = /^.+@.+\..+$/;

      if (!value || value.length === 0) {
        callback();
      } else if (!re.test(value) || value.length >= 256 || value.includes(' ')) {
        callback('Enter a valid Email');
      }
      callback();
    };

    return {
      guest: {
        firstName: this.existingGuestData.firstName || defaultGuestData.firstName,
        lastName: this.existingGuestData.lastName || defaultGuestData.lastName,
        email: this.existingGuestData.email || defaultGuestData.email,
        status: this.existingGuestData.status ?? defaultGuestData.status,
        vip: this.existingGuestData.vip ?? defaultGuestData.vip,
        totalExtraGuests: this.existingGuestData.totalExtraGuests ?? defaultGuestData.totalExtraGuests,
        phoneNumber: this.existingGuestData.phoneNumber || defaultGuestData.phoneNumber,
        jobTitle: this.existingGuestData.jobTitle || defaultGuestData.jobTitle,
        organization: this.existingGuestData.organization || defaultGuestData.organization,
        note: this.existingGuestData.note ?? defaultGuestData.note,
        barcodeValue: this.existingGuestData.barcodeValue ?? defaultGuestData.barcodeValue,
      },
      photo: {
        edited: false,
        URL: this.guestPhoto,
        file: null,
      },
      hoveringAvatar: false,
      additionalInfoIsOpen: !!(this.existingGuestData.phoneNumber || this.existingGuestData.jobTitle
        || this.existingGuestData.organization || this.existingGuestData.note),
      guestDetailsFormRules: {
        firstName: [{
          validator: checkFirstName,
          trigger: 'blur',
        }],
        lastName: [{
          validator: checkLastName,
          trigger: 'blur',
        }],
        email: [{
          validator: validateEmail,
          trigger: 'blur',
        }],
        phoneNumber: [{
          validator: validatePhoneNumber,
          trigger: 'blur',
        }],
      },
    };
  },
  computed: {
    // Local example number, e.g. +61 412 345 678 in Australia; empty until the edge answers
    phonePlaceholder() {
      return this.$store.state.currency.isResolved
        ? phonePlaceholder({ country: this.$store.state.currency.country })
        : '';
    },
    guestFullName() {
      if (!this.firstNameFirstOrder) {
        return `${this.guest.lastName} ${this.guest.firstName}`;
      }

      return `${this.guest.firstName} ${this.guest.lastName}`;
    },
    avatarPhoto() {
      if (this.photo.file) {
        return URL.createObjectURL(this.photo.file);
      }
      // if (this.photo.URL) return this.photo.URL;
      if (this.guestPhoto && !this.photo.edited) return this.guestPhoto;
      return '';
    },
    QRCodeURL() {
      if (this.showBarcodeValueInput && !this.guest.barcodeValue) return '';
      if (this.QrCodeType === 'Code39') return getBarcodeURL(this.guest.barcodeValue);
      if (this.showBarcodeValueInput) return getQRCodeURL({ value: this.guest.barcodeValue });

      return getQRCodeURL({
        value: this.existingGuestData.id,
        defaultGuestIdFormat: true,
      });
    },
  },
  methods: {
    focusFirstInput() {
      if (this.firstNameFirstOrder) {
        this.$refs['input-first-name'].focus();

        return;
      }

      this.$refs['input-last-name'].focus();
    },
    onAvatarTouch(event, selectFile) { // Handling touch devices photo selector
      if (!this.avatarPhoto) {
        selectFile();
        event.preventDefault();
      } else if (!this.hoveringAvatar) {
        this.hoveringAvatar = true;
        event.preventDefault();
      }
    },
    onRemovePhotoPress(removeFile) {
      this.photo.edited = true;

      if (this.photo.file) removeFile();
    },
    submitForm() {
      this.$refs.form
        .validate()
        .then(() => {
          this.$emit('successful-submit', {
            guest: this.guest,
            photo: {
              file: this.photo.file,
              edited: this.photo.edited,
            },
          });
        })
        .catch((e) => e);
    },
    onAdditionalInfoShown() {
      this.$refs['additional-infos'].$el.scrollIntoView({ behavior: 'smooth' });
    },
  },
};
</script>

<style>
.icon-no-image-placeholder {
  @apply w-auto !important;

  --fa-primary-color: theme('colors.gray.200');
  --fa-secondary-color: theme('colors.primary.light');
  --fa-primary-opacity: 1;
  --fa-secondary-opacity: 1;
}

@screen mobile-m {
  .guest-grid {
    grid-template-columns: min-content auto;
  }
}

@screen tablet {
  .guest-grid {
    grid-template-columns: min-content auto auto;
  }
}

.guest-avatar {
  --avatar-size: theme('spacing.130');
}

/*.extra-guests-form-item {
  >>> .el-form-item__content {
    @apply pl-15;
  }

  >>> .el-form-item__label {
    @apply w-full;
  }
}*/
</style>
